// Gate-verified chip constants for the printed inductive encoder on the PCB stator.
//
// Pure TypeScript. No Angular / DI imports. Every later encoder geometry task
// (bands, TX/RX coils, target ring, footprints, DRC) consumes CHIP from here.
//
// GATE OUTCOME (see docs/research/2026-07-09-raa2p3200-gate.md for citations):
//   The spec assumed part RAA2P3200. The RAA2P3200 is a SINGLE-COIL device
//   (one transmitter + one SIN/COS receiver pair) and physically CANNOT provide
//   the dual-coil fine + coarse receiver tracks that the frozen EncoderNet
//   interface (RX_FINE_* + RX_COARSE_*) requires. That is a hard spec mismatch,
//   so per the brief's "select the closest RAA2P sibling" rule the gate-selected
//   part is the RAA2P3226 — the DUAL-COIL sibling in the same TSSOP-16 / E4GSP
//   package, same eddy-current principle, same 3.3 V / 5 V supply.
//
//   The RAA2P3226 supports the spec's fine=4 / coarse=1 assumption directly via
//   "One Periodic Reference Track Mode" (datasheet §4.4.3.1, Table 12): the
//   primary (fine) coil is configurable to 4/8/16/32 periods and the secondary
//   (coarse) coil is a single-period reference track. Vernier mode
//   (coarse = fine-1) is an ALTERNATIVE high-resolution config, NOT a
//   requirement, so coarse=1 is valid. Gate verdict: PASS.
//
// DEVIATIONS FROM THE SPEC ASSUMPTION (flagged, do not silently rely on the old part):
//   1. Part is RAA2P3226 (dual-coil), NOT RAA2P3200 (single-coil). Output modes
//      differ: RAA2P3226 has UART/ABI/Step-Dir/I2C but NO SPI and NO UVW
//      (RAA2P3200 had SPI/UVW but no I2C). ABI — needed by the motor — is present.
//   2. STOCK CONCERN: RAA2P3226E4GSP#HA0 was 0 in stock at Digi-Key on 2026-07-09
//      ($177.05/1, 32-week factory lead). It is orderable (backorder) but pricey
//      and long-lead. The assumed RAA2P3200 is cheap and in stock but unusable
//      for dual-coil absolute mode. Not a hard blocker — flagged for the buyer.
//   3. Pin 16 (VDDD) is an internally-regulated digital-supply rail that must have
//      its own 100 nF cap to GND and must NOT be strapped to VDD. The frozen
//      EncoderNet union has no VDDD member; it is mapped to 'VDD' here for
//      readability (it is a supply pin) with `name:'VDDD'` disambiguating it —
//      downstream routing (Task 7) MUST NOT tie pin 16 to pin 14.
//
// DESIGN-CHOICE VALUES (within datasheet limits; not datasheet-fixed):
//   The datasheet does not pin a reference LTx/CTx — the TX tank is coil-optimizer
//   driven, constrained only by the LC excitation window fLC = 2..5.5 MHz
//   (datasheet Table 7) and the split-cap resonance fTx = 1 / (2*pi*sqrt(L*CTx/2)).
//   We fix a nominal operating point for the downstream geometry tasks:
//     carrierHz  = 4.0 MHz  (mid-window)
//     txTankCapPF = 1000 pF per split cap (CTx1 = CTx2 = 1 nF; effective 500 pF)
//   and derive the allowed coil-inductance window from the 2..5.5 MHz carrier
//   limits at that cap:  L = 1 / ((2*pi*f)^2 * (CTx/2)).
//     f = 5.5 MHz -> L ~= 1675 nH  (minimum)
//     f = 2.0 MHz -> L ~= 12665 nH (maximum)
//     f = 4.0 MHz -> L ~= 3166 nH  (nominal target for Task 4)

export interface ChipSpec {
  partNumber: string;                    // 'RAA2P3200' or gate-selected sibling
  package: 'TSSOP-16' | string;
  supplyVolts: number;                   // 3.3 or 5
  txInductanceMinNH: number;             // allowed TX tank window
  txInductanceMaxNH: number;
  txTankCapPF: number;                   // recommended C for the target carrier
  carrierHz: number;                     // nominal oscillator frequency
  finePeriodsAllowed: number[];          // dual-coil fine periodicities the part supports
  coarsePeriods: number;                 // coarse track periodicity for absolute mode (expected 1)
  outputModes: string[];                 // e.g. ['ABI','UVW','StepDir','SPI','PWM']
  pins: Array<{ n: number; name: string; net: EncoderNet }>;
  provisional: boolean;                  // true until the gate task verifies against the datasheet
}

export type EncoderNet =
  | 'TX_P' | 'TX_N'
  | 'RX_FINE_SIN_P' | 'RX_FINE_SIN_N' | 'RX_FINE_COS_P' | 'RX_FINE_COS_N'
  | 'RX_COARSE_SIN_P' | 'RX_COARSE_SIN_N' | 'RX_COARSE_COS_P' | 'RX_COARSE_COS_N'
  | 'VDD' | 'GND' | 'OUT_A' | 'OUT_B' | 'OUT_I' | 'CFG_SDA' | 'CFG_SCL';

export const CHIP: ChipSpec = {
  partNumber: 'RAA2P3226',
  package: 'TSSOP-16',
  supplyVolts: 3.3,               // datasheet: programmable 3.3 V +/-0.3 V or 5.0 V +/-0.5 V; 3.3 V chosen for the motor logic rail
  txInductanceMinNH: 1675,        // derived: fLC = 5.5 MHz (upper) at CTx = 1 nF
  txInductanceMaxNH: 12665,       // derived: fLC = 2.0 MHz (lower) at CTx = 1 nF
  txTankCapPF: 1000,              // CTx1 = CTx2 = 1 nF split caps (effective series 500 pF)
  carrierHz: 4_000_000,           // nominal, mid-window of datasheet fLC = 2..5.5 MHz
  finePeriodsAllowed: [4, 8, 16, 32],  // §4.4.3.1 / Table 12: primary coil periods in One-Track mode
  coarsePeriods: 1,               // §4.4.3.1: secondary coil = single-period reference track
  outputModes: ['UART', 'ABI', 'StepDir', 'I2C'],  // datasheet title/§9; no SPI, no UVW on this part
  pins: [
    // Receiver coil set #2 = secondary = COARSE reference track (pins 1-4)
    { n: 1, name: 'RX8', net: 'RX_COARSE_COS_P' },
    { n: 2, name: 'RX7', net: 'RX_COARSE_SIN_P' },
    { n: 3, name: 'RX6', net: 'RX_COARSE_COS_N' },
    { n: 4, name: 'RX5', net: 'RX_COARSE_SIN_N' },
    // Receiver coil set #1 = primary = FINE track (pins 5-8)
    { n: 5, name: 'RX4', net: 'RX_FINE_COS_P' },
    { n: 6, name: 'RX3', net: 'RX_FINE_SIN_P' },
    { n: 7, name: 'RX2', net: 'RX_FINE_COS_N' },
    { n: 8, name: 'RX1', net: 'RX_FINE_SIN_N' },
    // Transmitter tank (pins 9, 11) — coil between TX1/TX2 via RTx, split caps CTx1/CTx2 to GND
    { n: 9, name: 'TX1', net: 'TX_P' },
    { n: 11, name: 'TX2', net: 'TX_N' },
    // Digital I/O — ABI output mode (config I2C SCL/SDA share pins 10/12 at programming time)
    { n: 10, name: 'IO1', net: 'OUT_A' },    // ABI: A  (also I2C SCL / aux analog in)
    { n: 12, name: 'OUT2', net: 'OUT_B' },   // ABI: B  (also I2C SDA / UART TxD)
    { n: 13, name: 'OUT1', net: 'OUT_I' },   // ABI: Index (also single-wire programming)
    // Supplies
    { n: 14, name: 'VDD', net: 'VDD' },      // external supply 3.3 V / 5 V
    { n: 15, name: 'GND', net: 'GND' },
    { n: 16, name: 'VDDD', net: 'VDD' },     // DEVIATION #3: internally-regulated rail; 100 nF to GND, do NOT strap to VDD
  ],
  provisional: false,
};
